"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useContext, useMemo, useState, useEffect, useRef, useCallback } from "react";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { AnimatePresence, motion } from "framer-motion";
import type { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveType } from "@/generated/models/ApiWaveType";
import { WaveLeaderboardTime } from "@/components/waves/leaderboard/WaveLeaderboardTime";
import { WaveLeaderboardHeader } from "@/components/waves/leaderboard/header/WaveleaderboardHeader";
import { WaveDropCreate } from "@/components/waves/leaderboard/create/WaveDropCreate";
import { WaveLeaderboardCurationDropModal } from "@/components/waves/leaderboard/create/WaveLeaderboardCurationDropModal";
import { WaveLeaderboardDrops } from "@/components/waves/leaderboard/drops/WaveLeaderboardDrops";
import { WaveLeaderboardGallery } from "@/components/waves/leaderboard/gallery/WaveLeaderboardGallery";
import { WaveLeaderboardGrid } from "@/components/waves/leaderboard/grid/WaveLeaderboardGrid";
import { isLeaderboardViewMode, type LeaderboardViewMode } from "@/components/waves/leaderboard/types";
import { useWave } from "@/hooks/useWave";
import { AuthContext } from "@/components/auth/Auth";
import { useLayout } from "./layout/LayoutContext";
import { WaveDropsLeaderboardSort } from "@/hooks/useWaveDropsLeaderboard";
import useLocalPreference from "@/hooks/useLocalPreference";
import MemesArtSubmissionModal from "@/components/waves/memes/MemesArtSubmissionModal";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useWaveCurationGroups } from "@/hooks/waves/useWaveCurationGroups";
import { getWaveDropEligibility } from "@/components/waves/leaderboard/dropEligibility";
interface MyStreamWaveLeaderboardProps {
  readonly wave: ApiWave;
  readonly onDropClick: (drop: ExtendedDrop) => void;
}
const MyStreamWaveLeaderboard: React.FC<MyStreamWaveLeaderboardProps> = t0 => {
  const $ = _c(92);
  const {
    wave,
    onDropClick
  } = t0;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const {
    connectedProfile,
    activeProfileProxy
  } = useContext(AuthContext);
  const {
    isMemesWave,
    isCurationWave,
    participation
  } = useWave(wave);
  const {
    leaderboardViewStyle
  } = useLayout();
  const mountedRef = useRef(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => () => {
      mountedRef.current = false;
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const [isCreateDropOpen, setIsCreateDropOpen] = useState(false);
  const [isMemesCreateOpen, setIsMemesCreateOpen] = useState(false);
  const [isCurationDropModalOpen, setIsCurationDropModalOpen] = useState(false);
  const [minPrice, setMinPrice] = useState(undefined);
  const [maxPrice, setMaxPrice] = useState(undefined);
  const isLoggedIn = Boolean(connectedProfile?.handle);
  const t3 = Boolean(activeProfileProxy);
  let t4;
  if ($[2] !== isCurationWave || $[3] !== isLoggedIn || $[4] !== participation || $[5] !== t3) {
    t4 = getWaveDropEligibility({
      isLoggedIn,
      isProxy: t3,
      isCurationWave,
      participation
    });
    $[2] = isCurationWave;
    $[3] = isLoggedIn;
    $[4] = participation;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    canCreateDrop
  } = t4;
  const showToggleableDropInput = !isMemesWave && !isCurationWave && isCreateDropOpen;
  let t5;
  if ($[7] !== canCreateDrop || $[8] !== isCurationWave || $[9] !== isMemesWave) {
    t5 = () => {
      if (!mountedRef.current) {
        return;
      }
      if (isMemesWave) {
        setIsMemesCreateOpen(true);
        return;
      }
      if (isCurationWave) {
        if (!canCreateDrop) {
          return;
        }
        setIsCurationDropModalOpen(true);
        return;
      }
      if (!isCurationWave) {
        setIsCreateDropOpen(true);
      }
    };
    $[7] = canCreateDrop;
    $[8] = isCurationWave;
    $[9] = isMemesWave;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onCreateDrop = t5;
  const viewPreferenceKey = `waveViewMode_${wave.id}`;
  const defaultViewMode = isMemesWave ? "grid" : "list";
  const [viewMode, setViewMode] = useLocalPreference(viewPreferenceKey, defaultViewMode, isLeaderboardViewMode);
  const sortPreferenceKey = `waveSortMode_${wave.id}`;
  let t6;
  if ($[11] !== isCurationWave) {
    t6 = value => value === WaveDropsLeaderboardSort.RANK || value === WaveDropsLeaderboardSort.RATING_PREDICTION || value === WaveDropsLeaderboardSort.TREND || value === WaveDropsLeaderboardSort.MY_REALTIME_VOTE || value === WaveDropsLeaderboardSort.CREATED_AT || isCurationWave && value === WaveDropsLeaderboardSort.PRICE;
    $[11] = isCurationWave;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const [sort, setSort] = useLocalPreference(sortPreferenceKey, WaveDropsLeaderboardSort.RANK, t6);
  const t7 = wave.wave.type !== ApiWaveType.Chat;
  let t8;
  if ($[13] !== t7 || $[14] !== wave.id) {
    t8 = {
      waveId: wave.id,
      enabled: t7
    };
    $[13] = t7;
    $[14] = wave.id;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const {
    data: t9,
    isLoading: isLoadingCurationGroups,
    isError: isCurationGroupsError
  } = useWaveCurationGroups(t8);
  let t10;
  if ($[16] !== t9) {
    t10 = t9 === undefined ? [] : t9;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const curationGroups = t10;
  let t11;
  if ($[18] !== searchParams) {
    t11 = searchParams.get("curated_by_group");
    $[18] = searchParams;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const rawCuratedByGroupId = t11;
  const curationGroupIdSet = new Set(curationGroups.map(_temp));
  let t12;
  bb0: {
    if (!rawCuratedByGroupId) {
      t12 = undefined;
      break bb0;
    }
    if (isCurationGroupsError) {
      t12 = undefined;
      break bb0;
    }
    if (isLoadingCurationGroups) {
      t12 = rawCuratedByGroupId;
      break bb0;
    }
    t12 = curationGroupIdSet.has(rawCuratedByGroupId) ? rawCuratedByGroupId : undefined;
  }
  const curatedByGroupId = t12;
  let t13;
  bb1: {
    const hasPriceFilter = typeof minPrice === "number" || typeof maxPrice === "number";
    if (isCurationWave && (hasPriceFilter || sort === WaveDropsLeaderboardSort.PRICE)) {
      t13 = "ETH";
      break bb1;
    }
    t13 = undefined;
  }
  const priceCurrency = t13;
  let t14;
  if ($[20] !== pathname || $[21] !== router || $[22] !== searchParams) {
    t14 = groupId => {
      const nextParams = new URLSearchParams(searchParams.toString());
      if (groupId) {
        nextParams.set("curated_by_group", groupId);
      } else {
        nextParams.delete("curated_by_group");
      }
      const nextQuery = nextParams.toString();
      const nextUrl = nextQuery ? `${pathname}?${nextQuery}` : pathname;
      router.replace(nextUrl, {
        scroll: false
      });
    };
    $[20] = pathname;
    $[21] = router;
    $[22] = searchParams;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const updateCurationGroupInUrl = t14;
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = t16 => {
      const {
        minPrice: nextMinPrice,
        maxPrice: nextMaxPrice
      } = t16;
      setMinPrice(nextMinPrice);
      setMaxPrice(nextMaxPrice);
    };
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const updatePriceRange = t15;
  let t16;
  bb2: {
    if (!isMemesWave) {
      t16 = viewMode;
      break bb2;
    }
    if (viewMode === "grid_content_only") {
      t16 = "grid";
      break bb2;
    }
    t16 = viewMode;
  }
  const effectiveViewMode = t16;
  let leaderboardContent;
  if (effectiveViewMode === "list") {
    let t17;
    if ($[25] !== curatedByGroupId || $[26] !== maxPrice || $[27] !== minPrice || $[28] !== onCreateDrop || $[29] !== priceCurrency || $[30] !== sort || $[31] !== wave) {
      t17 = <WaveLeaderboardDrops wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onCreateDrop={onCreateDrop} />;
      $[25] = curatedByGroupId;
      $[26] = maxPrice;
      $[27] = minPrice;
      $[28] = onCreateDrop;
      $[29] = priceCurrency;
      $[30] = sort;
      $[31] = wave;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    leaderboardContent = t17;
  } else {
    if (!isMemesWave) {
      const t17 = effectiveViewMode === "grid" ? "compact" : "content_only";
      let t18;
      if ($[33] !== curatedByGroupId || $[34] !== maxPrice || $[35] !== minPrice || $[36] !== onDropClick || $[37] !== priceCurrency || $[38] !== sort || $[39] !== t17 || $[40] !== wave) {
        t18 = <WaveLeaderboardGrid wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} mode={t17} onDropClick={onDropClick} />;
        $[33] = curatedByGroupId;
        $[34] = maxPrice;
        $[35] = minPrice;
        $[36] = onDropClick;
        $[37] = priceCurrency;
        $[38] = sort;
        $[39] = t17;
        $[40] = wave;
        $[41] = t18;
      } else {
        t18 = $[41];
      }
      leaderboardContent = t18;
    } else {
      let t17;
      if ($[42] !== curatedByGroupId || $[43] !== maxPrice || $[44] !== minPrice || $[45] !== onDropClick || $[46] !== priceCurrency || $[47] !== sort || $[48] !== wave) {
        t17 = <WaveLeaderboardGallery wave={wave} sort={sort} curatedByGroupId={curatedByGroupId} minPrice={minPrice} maxPrice={maxPrice} priceCurrency={priceCurrency} onDropClick={onDropClick} />;
        $[42] = curatedByGroupId;
        $[43] = maxPrice;
        $[44] = minPrice;
        $[45] = onDropClick;
        $[46] = priceCurrency;
        $[47] = sort;
        $[48] = wave;
        $[49] = t17;
      } else {
        t17 = $[49];
      }
      leaderboardContent = t17;
    }
  }
  let t17;
  if ($[50] !== wave) {
    t17 = <WaveLeaderboardTime wave={wave} />;
    $[50] = wave;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== setViewMode) {
    t18 = mode => setViewMode(mode);
    $[52] = setViewMode;
    $[53] = t18;
  } else {
    t18 = $[53];
  }
  let t19;
  if ($[54] !== setSort) {
    t19 = s => setSort(s);
    $[54] = setSort;
    $[55] = t19;
  } else {
    t19 = $[55];
  }
  const t20 = curatedByGroupId ?? null;
  const t21 = curationGroups.length > 0 ? updateCurationGroupInUrl : undefined;
  const t22 = isCurationWave ? updatePriceRange : undefined;
  let t23;
  if ($[56] !== curationGroups || $[57] !== effectiveViewMode || $[58] !== maxPrice || $[59] !== minPrice || $[60] !== onCreateDrop || $[61] !== sort || $[62] !== t18 || $[63] !== t19 || $[64] !== t20 || $[65] !== t21 || $[66] !== t22 || $[67] !== wave) {
    t23 = <div className="tw-sticky tw-top-0 tw-z-30 tw-bg-black tw-py-4"><WaveLeaderboardHeader wave={wave} viewMode={effectiveViewMode} sort={sort} onViewModeChange={t18} onCreateDrop={onCreateDrop} onSortChange={t19} curationGroups={curationGroups} curatedByGroupId={t20} onCurationGroupChange={t21} minPrice={minPrice} maxPrice={maxPrice} onPriceRangeChange={t22} /></div>;
    $[56] = curationGroups;
    $[57] = effectiveViewMode;
    $[58] = maxPrice;
    $[59] = minPrice;
    $[60] = onCreateDrop;
    $[61] = sort;
    $[62] = t18;
    $[63] = t19;
    $[64] = t20;
    $[65] = t21;
    $[66] = t22;
    $[67] = wave;
    $[68] = t23;
  } else {
    t23 = $[68];
  }
  let t24;
  if ($[69] !== showToggleableDropInput || $[70] !== wave) {
    t24 = showToggleableDropInput && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: "auto"
    }} exit={{
      opacity: 0,
      height: 0
    }} transition={{
      duration: 0.2,
      ease: "easeInOut"
    }}><WaveDropCreate wave={wave} onCancel={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} onSuccess={() => {
        if (mountedRef.current) {
          setIsCreateDropOpen(false);
        }
      }} /></motion.div>;
    $[69] = showToggleableDropInput;
    $[70] = wave;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== t24) {
    t25 = <AnimatePresence>{t24}</AnimatePresence>;
    $[72] = t24;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== isMemesCreateOpen || $[75] !== isMemesWave || $[76] !== wave) {
    t26 = isMemesWave && isMemesCreateOpen && <MemesArtSubmissionModal isOpen={isMemesCreateOpen} wave={wave} onClose={() => setIsMemesCreateOpen(false)} />;
    $[74] = isMemesCreateOpen;
    $[75] = isMemesWave;
    $[76] = wave;
    $[77] = t26;
  } else {
    t26 = $[77];
  }
  let t27;
  if ($[78] !== isCurationDropModalOpen || $[79] !== isCurationWave || $[80] !== wave) {
    t27 = isCurationWave && isCurationDropModalOpen && <WaveLeaderboardCurationDropModal isOpen={isCurationDropModalOpen} wave={wave} onClose={() => setIsCurationDropModalOpen(false)} />;
    $[78] = isCurationDropModalOpen;
    $[79] = isCurationWave;
    $[80] = wave;
    $[81] = t27;
  } else {
    t27 = $[81];
  }
  let t28;
  if ($[82] !== leaderboardContent || $[83] !== t25 || $[84] !== t26 || $[85] !== t27) {
    t28 = <div>{t25}{t26}{t27}{leaderboardContent}</div>;
    $[82] = leaderboardContent;
    $[83] = t25;
    $[84] = t26;
    $[85] = t27;
    $[86] = t28;
  } else {
    t28 = $[86];
  }
  let t29;
  if ($[87] !== leaderboardViewStyle || $[88] !== t17 || $[89] !== t23 || $[90] !== t28) {
    t29 = <div className="tw-w-full tw-flex tw-flex-col tw-rounded-t-xl tw-overflow-y-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300 tw-overflow-x-hidden tw-flex-grow tw-px-2 sm:tw-px-4" style={leaderboardViewStyle}>{t17}{t23}{t28}</div>;
    $[87] = leaderboardViewStyle;
    $[88] = t17;
    $[89] = t23;
    $[90] = t28;
    $[91] = t29;
  } else {
    t29 = $[91];
  }
  return t29;
};
export default MyStreamWaveLeaderboard;
function _temp(group) {
  return group.id;
}